import { gql } from '@apollo/client';

export const CURRENT_USER = gql`
	query currentUser {
		currentUser {
			_id
			name
			role
			departmentIds
			email
			phone
			address {
				street_address
				postal_code
				locality
				country
			}
			citizenship
			updatedAt
			userVerifications {
				type
				value
				isVerified
				lastVerified
			}
			forceUserVerificationDate
			userLastVerificationDate
			isPoliticallyExposedPerson
			userPoliticallyExposedPersonRoles {
				type
				name
				address
			}
		}
	}
`;

export const FIND_ONE_USER = gql`
	query findOneUser($_id: ID!) {
		user: findOneUser(_id: $_id) {
			_id
			name
			role
			position
			email
			phone
			address {
				street_address
				postal_code
				locality
				country
			}
			socialno
			dNumber
		}
	}
`;

export const FIND_ONE_USER_BASIC_INFO = gql`
	query findOneUserBasicInfo($_id: ID!) {
		user: findOneUserBasicInfo(_id: $_id) {
			_id
			name
			role
			email
			phone
		}
	}
`;

export const FIND_USER_WITH_USER_ROLES = gql`
	query findOneUser($_id: ID!) {
		user: findOneUser(_id: $_id) {
			_id
			name
			role
			departmentIds
			position
			hasLoggedIn
			auditingCompanyId
			email
			phone
			address {
				street_address
				postal_code
				locality
				country
			}
			citizenship
			socialno
			dNumber
			userVerifications {
				type
				lastSent
				isVerified
				lastVerified
			}
			userRoles {
				key
				id
				companyName
				role
				roleLabel
			}
			uniqueCompanyUserRoles {
				company {
					_id
					name
				}
				roles {
					role
					roleLabel
				}
			}
			ownerTenants {
				_id
				name
				owners {
					user {
						_id
					}
					shareInPercent
				}
			}
			accountantTenants: adminCompanies(roles: [accountant]) {
				_id
				name
			}
			projectManagerTenants: adminCompanies(roles: [projectManager]) {
				_id
				name
			}
			accountManagerTenants: adminCompanies(roles: [accountManager]) {
				_id
				name
			}
			payrollManagerTenants: adminCompanies(roles: [payrollManager]) {
				_id
				name
			}
			annualAccountsManagerTenants: adminCompanies(
				roles: [annualAccountsManager]
			) {
				_id
				name
			}
			advisorTenants: adminCompanies(roles: [advisor]) {
				_id
				name
			}
			controllerTenants: adminCompanies(roles: [controller]) {
				_id
				name
			}
			auditorTenants: adminCompanies(roles: [auditor]) {
				_id
				name
			}
			folderAccess {
				tenantId
				folders
			}
			isPoliticallyExposedPerson
			userPoliticallyExposedPersonRoles {
				type
				name
				address
			}
			identificationFile
			forceUserVerificationDate
			userLastVerificationDate
			createdAt
			createdByDisplayName
			updatedAt
			updatedByDisplayName
		}
	}
`;

export const PAGINATE_USERS = gql`
	query paginateUsers(
		$order: Int
		$orderBy: String
		$limit: Int
		$offset: Int
		$role: String
		$roles: [String]
		$search: String
		$filter: AppFilterInput
	) {
		users: paginateUsers(
			order: $order
			orderBy: $orderBy
			limit: $limit
			skip: $offset
			role: $role
			roles: $roles
			search: $search
			filter: $filter
		) {
			count
			items {
				_id
				name
				email
				phone
				address {
					street_address
					postal_code
					locality
					country
				}
				role
				roleName
				identificationFile
				isPoliticallyExposedPerson
				userVerifications {
					type
					isVerified
				}
			}
		}
	}
`;

export const PAGINATE_USERS_FOR_FILTER_SELECT = gql`
	query paginateUsers($order: Int, $orderBy: String, $roles: [String]) {
		users: paginateUsers(order: $order, orderBy: $orderBy, roles: $roles) {
			count
			items {
				_id
				name
			}
		}
	}
`;

export const PAGINATE_AUDITORS = gql`
	query paginateAuditors(
		$order: Int
		$orderBy: String
		$limit: Int
		$offset: Int
		$search: String
		$filter: AppFilterInput
	) {
		auditors: paginateUsers(
			order: $order
			orderBy: $orderBy
			limit: $limit
			skip: $offset
			search: $search
			role: "auditor"
			filter: $filter
		) {
			count
			items {
				_id
				name
				auditingCompany {
					name
				}
				auditorCompanies: adminCompanies(roles: [auditor]) {
					_id
				}
			}
		}
	}
`;

export const FIND_TENANT_USERS = gql`
	query findTenantUsers($tenantId: ID!) {
		users: findTenantUsers(tenantId: $tenantId) {
			count
			items {
				_id
				name
				role
				email
				phone
			}
		}
	}
`;

export const FIND_ADMIN_USERS = gql`
	query findAdminUsers {
		users: findAdminUsers {
			count
			items {
				_id
				name
				role
				email
				phone
			}
		}
	}
`;

export const FIND_USER_INVITE_TOKEN = gql`
	query findUserInviteToken($userId: ID!) {
		token: findUserInviteToken(userId: $userId)
	}
`;

export const FIND_TENANT_BOARD_MEMBER_USERS = gql`
	query findTenantBoardMemberUsers($tenantId: ID!) {
		users: findTenantBoardMemberUsers(tenantId: $tenantId) {
			count
			items {
				_id
				name
				role
				email
				phone
			}
		}
	}
`;

export const PAGINATE_ACCOUNTANTS = gql`
	query paginateAccountants(
		$order: Int
		$orderBy: String
		$limit: Int
		$offset: Int
		$search: String
		$filter: AppFilterInput
	) {
		accountants: paginateAccountants(
			order: $order
			orderBy: $orderBy
			limit: $limit
			skip: $offset
			search: $search
			filter: $filter
		) {
			count
			items {
				_id
				name
				position
				roleName
				# accountantTenants: adminCompanies(roles: [accountant]) {
				# 	_id
				# }
				# accountManagerTenants: adminCompanies(roles: [accountManager]) {
				# 	_id
				# }
				# controllerTenants: adminCompanies(roles: [controller]) {
				# 	_id
				# }
			}
		}
	}
`;

export const FIND_USER_PROFILE = gql`
	query findUserProfile {
		user: findUserProfile {
			_id
			name
			email
			phone
			address {
				street_address
				postal_code
				locality
				country
			}
			citizenship
			isPoliticallyExposedPerson
			userPoliticallyExposedPersonRoles {
				type
				name
				address
			}
		}
	}
`;

export const CAN_UPDATE_USER = gql`
	query canUpdateUser($userId: ID!) {
		permission: canUpdateUser(userId: $userId) {
			canUpdate
			reason
		}
	}
`;
